import type React from 'react';
import { useState } from 'react';

import { TypeButton } from '../../../../../utils/button';
import NotificationModal from '../../../../../utils/modal';
import { type IVolunteerRoleOption, VOLUNTEER_ROLE_OPTIONS } from '../../helper';
import {
	InputError,
	LearnMoreLink,
	ModalContent,
	ModalFooter,
	ModalList,
	ModalSection,
	ModalSectionText,
	ModalSectionTitle,
	RoleLabelText,
	RoleOptionLeft,
	RoleOptionWrapper,
	RoleRadioCustom,
	RoleSelectorContainer,
	RoleSelectorLabel,
} from './styled';

interface Props {
	value: string;
	onChange: (value: string) => void;
	error?: string;
}

const VolunteerRoleSelector = ({ value, onChange, error }: Props) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedRole, setSelectedRole] = useState<IVolunteerRoleOption | null>(null);

	const handleRoleSelect = (roleValue: string) => {
		onChange(roleValue);
	};

	const handleLearnMore = (e: React.MouseEvent, role: IVolunteerRoleOption) => {
		e.stopPropagation();
		setSelectedRole(role);
		setModalOpen(true);
	};

	return (
		<>
			<RoleSelectorContainer>
				<RoleSelectorLabel>
					Choose your volunteer role? <span>*</span>
				</RoleSelectorLabel>

				{VOLUNTEER_ROLE_OPTIONS.map((role) => (
					<RoleOptionWrapper
						key={role.id}
						selected={value === role.value}
						onClick={() => handleRoleSelect(role.value)}
						role='radio'
						aria-checked={value === role.value}
						tabIndex={0}
						onKeyDown={(e: React.KeyboardEvent) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								handleRoleSelect(role.value);
							}
						}}
					>
						<RoleOptionLeft>
							<RoleRadioCustom selected={value === role.value} />
							<RoleLabelText>{role.label}</RoleLabelText>
						</RoleOptionLeft>
						<LearnMoreLink
							type='button'
							onClick={(e: React.MouseEvent) => handleLearnMore(e, role)}
							aria-label={`Learn more about ${role.label}`}
						>
							Learn more
						</LearnMoreLink>
					</RoleOptionWrapper>
				))}

				{error && <InputError>{error}</InputError>}
			</RoleSelectorContainer>

			<NotificationModal
				openModal={modalOpen}
				setOpenModal={setModalOpen}
				title={selectedRole?.description.title || 'Role Details'}
			>
				{selectedRole && (
					<ModalContent>
						<ModalSection>
							<ModalSectionTitle>Summary</ModalSectionTitle>
							<ModalSectionText>{selectedRole.description.summary}</ModalSectionText>
						</ModalSection>

						<ModalSection>
							<ModalSectionTitle>Responsibilities</ModalSectionTitle>
							<ModalList>
								{selectedRole.description.responsibilities.map((item, index) => (
									<li key={index}>{item}</li>
								))}
							</ModalList>
						</ModalSection>

						<ModalSection>
							<ModalSectionTitle>Requirements</ModalSectionTitle>
							<ModalList>
								{selectedRole.description.requirements.map((item, index) => (
									<li key={index}>{item}</li>
								))}
							</ModalList>
						</ModalSection>

						<ModalFooter>
							<TypeButton
								title='Dismiss'
								onClick={() => setModalOpen(false)}
								buttonType='outlined'
							/>
						</ModalFooter>
					</ModalContent>
				)}
			</NotificationModal>
		</>
	);
};

export default VolunteerRoleSelector;
